import { Priority, RegulatoryValue, Severity } from '../../occurrence-review/models/occurrence-review';

export interface PrioritisationRegulatoryValue {
  Key: RegulatoryValue;
  Label: string;
}

export interface PrioritisationSeverity {
  Key: Severity;
  Label: string;
  Descriptors: string[];
}

export interface PrioritisationPriority {
  Key: Priority;
  Label: string;
}

export interface PrioritisationConfigCell {
  SeverityKey: Severity;
  RegulatoryValueKey: RegulatoryValue;
  PriorityKey: Priority;
  Descriptions: string[];
}

export interface PrioritisationFollowUps {
  PriorityKey: Priority;
  Options: string[];
}

export interface PrioritisationConfig {
  Id: number;
  RegulatoryValues: PrioritisationRegulatoryValue[];
  Severities: PrioritisationSeverity[];
  Priorities: PrioritisationPriority[];
  Cells: PrioritisationConfigCell[];
  FollowUps: PrioritisationFollowUps[];
}

export function configLabel(items: { Key: string; Label: string }[], key: string): string {
  return items.find((item) => item.Key === key)?.Label ?? key;
}

export function updateWhere<T>(items: T[], match: (item: T) => boolean, patch: Partial<T>): T[] {
  return items.map((item) => (match(item) ? { ...item, ...patch } : item));
}

export function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function createDefaultPrioritisationConfig(): PrioritisationConfig {
  return {
    Id: 1,
    RegulatoryValues: [
      { Key: 'Marginal', Label: 'Marginal' },
      { Key: 'Moderate', Label: 'Moderate' },
      { Key: 'Substantial', Label: 'Substantial' },
    ],
    Severities: [
      {
        Key: 'Major',
        Label: 'Major',
        Descriptors: ['Fatality', 'Multiple major injuries'],
      },
      {
        Key: 'Significant',
        Label: 'Significant',
        Descriptors: [
          'Significant near miss with potential for fatalities / multiple major injuries',
          'Significant failure of key risk controls',
          'Single major injury',
          'Credible intelligence of significant deficiencies in key risk controls',
        ],
      },
      {
        Key: 'Minor',
        Label: 'Minor',
        Descriptors: [
          'Minor injuries',
          'Near miss with limited potential for significant harm',
          'Risk control systems functioned as intended to manage occurrence',
          'Unsubstantiated intelligence of deficiencies in operators SMS',
        ],
      },
    ],
    Priorities: [
      { Key: 'Very High', Label: 'Very High' },
      { Key: 'High', Label: 'High' },
      { Key: 'Medium', Label: 'Medium' },
      { Key: 'Low', Label: 'Low' },
    ],
    Cells: [
      {
        SeverityKey: 'Major',
        RegulatoryValueKey: 'Marginal',
        PriorityKey: 'Medium',
        Descriptions: [
          'Collision at a level crossing where all railway systems are compliant with relevant standards, have functioned correctly and no harm to railway workers and passengers. Limited potential for any safety lessons to be learnt from this specific occurrence and safety program in place to drive broader safety outcomes',
          'Occurrence is principally WHS/environmental in nature and decision confirmed that another regulator will manage any follow-up',
        ],
      },
      {
        SeverityKey: 'Major',
        RegulatoryValueKey: 'Moderate',
        PriorityKey: 'Very High',
        Descriptions: [
          "Operator has strong safety performance and there is a high level of confidence in the Operator's SMS to investigate and respond to lessons learnt",
          'No recent history of safety deficiencies / areas of concern related to the occurrence',
          'Unlikely to be ongoing / widespread public safety concern / media interest resulting from the occurrence',
        ],
      },
      {
        SeverityKey: 'Major',
        RegulatoryValueKey: 'Substantial',
        PriorityKey: 'Very High',
        Descriptions: [
          'Operator has poor safety performance and/or there is limited confidence the occurrence will be effectively investigated and safety deficiencies addressed',
          'Operator has a history of non-conformances and/or known safety deficiencies / areas of concern relating to this (or similar) occurrence',
          'Likely to be ongoing / widespread public safety concern / media interest resulting from the occurrence',
        ],
      },
      {
        SeverityKey: 'Significant',
        RegulatoryValueKey: 'Marginal',
        PriorityKey: 'Low',
        Descriptions: [
          'Type and nature of occurrence is well understood, and performance is being actively monitored and managed through existing regulatory strategy',
          'Broader coordinated safety program in place to address this type of occurrence',
          'Limited potential for any safety lessons to be learnt from this specific occurrence',
          'Occurrence is principally WHS/environmental in nature and decision confirmed that another regulator will manage any follow-up',
        ],
      },
      {
        SeverityKey: 'Significant',
        RegulatoryValueKey: 'Moderate',
        PriorityKey: 'High',
        Descriptions: [
          "Operator has a strong safety performance and there is a high level of confidence in the Operator's SMS to investigate and respond to lessons learnt",
          'No recent history of safety deficiencies / areas of concern related to the occurrence',
          'Potential for systemic issues that could lead to a major accident',
          'Unlikely to be ongoing / widespread public safety concern / media interest resulting from the occurrence',
        ],
      },
      {
        SeverityKey: 'Significant',
        RegulatoryValueKey: 'Substantial',
        PriorityKey: 'High',
        Descriptions: [
          'Operator has poor safety performance and/or there is limited confidence the occurrence will be effectively investigated and safety deficiencies addressed',
          'Reasonable concern that there are systemic issues that need urgent attention to prevent a major accident',
          'Potential for ongoing / widespread public safety concern / media interest resulting from the occurrence',
        ],
      },
      {
        SeverityKey: 'Minor',
        RegulatoryValueKey: 'Marginal',
        PriorityKey: 'Low',
        Descriptions: [
          'Low level compliance issues with no significant potential to result in harm',
          'Further action unlikely to meaningfully inform / contribute to the operator regulatory strategy',
          'Broader coordinated safety program in place to address this type of occurrence',
        ],
      },
      {
        SeverityKey: 'Minor',
        RegulatoryValueKey: 'Moderate',
        PriorityKey: 'Low',
        Descriptions: [
          'Compliance issues with the potential to result in harm however other (effective) controls significantly reduce the risk',
          'Limited potential for any safety lessons to be learnt from this occurrence in isolation, however occurrence/trends should be considered when developing the operator regulatory strategy',
        ],
      },
      {
        SeverityKey: 'Minor',
        RegulatoryValueKey: 'Substantial',
        PriorityKey: 'Medium',
        Descriptions: [
          'Compliance issues with the potential to result in harm and limited confidence in the effectiveness of other controls to reduce the risk',
        ],
      },
    ],
    FollowUps: [
      {
        PriorityKey: 'Very High',
        Options: [
          'Immediate escalation to Investigations.',
          'Refer to Intelligence Review Group.',
          'Consider targeted regulatory action/activity.',
          'Consider commencing enquiry.',
          'Inform future regulatory strategy (ORS).',
        ],
      },
      {
        PriorityKey: 'High',
        Options: [
          'Refer to Intelligence Review Group.',
          'Consider escalation to National Investigations Committee (NIC) and/or Investigations.',
          'Consider escalation to Safety Policy and Regulatory Committee (SPARC).',
          'Consider escalation to COO / National Operations Committee (NOC).',
          'Consider targeted regulatory action/activity.',
          'Consider commencing enquiry.',
          'Consider active monitoring/trending or adding to watch-list.',
          'Inform future regulatory strategy (ORS).',
        ],
      },
      {
        PriorityKey: 'Medium',
        Options: [
          'Refer to Intelligence Review Group.',
          'Inform future regulatory strategy (ORS).',
          'Consider active monitoring/trending or adding to watch-list.',
          'No further action - active monitoring/trending in place to inform ORS.',
          'No further action - broader coordinated safety program in place.',
          'No further action - referred to other relevant regulator/authority.',
        ],
      },
      {
        PriorityKey: 'Low',
        Options: [
          'No further action - active monitoring/trending in place to inform ORS.',
          'No further action - broader coordinated safety program in place.',
          'No further action - low regulatory value.',
          'No further action - referred to other relevant regulator/authority.',
          'Inform future regulatory strategy (ORS).',
        ],
      },
    ],
  };
}
